import React, { useEffect, useMemo, useState } from 'react';
import RoddoPageShell from './layout/RoddoPageShell';
import type { UserProfile } from '../types';
import { xpProgress } from '../utils/xp';
import { getDailyRewardPreview, type DailyActivityState } from '../services/dailyRewards';
import {
  getDashboardDailySnapshot,
  startDashboardDailyStore,
  subscribeDashboardDailyStore,
} from '../services/dashboardDailyStore';

type DashboardAction = () => void;

type DashboardProps = {
  userProfile?: UserProfile | null;
  catchLog?: any[];
  catchDiaryEntries?: any[];
  progressByKey?: Record<string, any>;
  completionsByKey?: Record<string, any>;
  achievementMap?: Record<string, any>;
  isDarkMode?: boolean;
  onOpenMap?: DashboardAction;
  onOpenFishIQ?: DashboardAction;
  onOpenLogCatch?: DashboardAction;
  onOpenCommunity?: DashboardAction;
  onOpenChallenges?: DashboardAction;
  onOpenAchievements?: DashboardAction;
  onOpenHold?: DashboardAction;
  onBack?: DashboardAction;
};

function compactNumber(value: unknown): string {
  const n = Number(value || 0);
  if (!Number.isFinite(n) || n <= 0) return '0';
  if (n >= 1000000) return `${(n / 1000000).toFixed(1)}m`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}k`;
  return String(Math.floor(n));
}

function uniqueCount(rows: any[], picker: (row: any) => unknown): number {
  const set = new Set<string>();
  rows.forEach((row) => {
    const raw = picker(row);
    const value = String(raw || '').trim().toLowerCase();
    if (value && value !== 'unknown') set.add(value);
  });
  return set.size;
}

function pct(value: unknown, target: unknown): number {
  const v = Number(value || 0);
  const t = Number(target || 0);
  if (!Number.isFinite(v) || !Number.isFinite(t) || t <= 0) return 0;
  return Math.max(0, Math.min(100, Math.round((v / t) * 100)));
}

function resolveChallengeTitle(entry: any): string {
  return String(entry?.title || entry?.name || entry?.challengeTitle || entry?.key || 'Active goal')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (m) => m.toUpperCase());
}

const Dashboard: React.FC<DashboardProps> = ({
  userProfile,
  catchLog = [],
  catchDiaryEntries = [],
  progressByKey = {},
  completionsByKey = {},
  achievementMap = {},
  onOpenMap,
  onOpenFishIQ,
  onOpenLogCatch,
  onOpenCommunity,
  onOpenChallenges,
  onOpenAchievements,
  onOpenHold,
  onBack,
  isDarkMode = false,
}) => {
  const uid = String((userProfile as any)?.uid || '').trim();
  const dailyDateKey = useMemo(() => getDailyRewardPreview(userProfile as any).today, [userProfile]);
  const [dailySnapshot, setDailySnapshot] = useState(() =>
    getDashboardDailySnapshot(uid, dailyDateKey),
  );

  useEffect(() => {
    startDashboardDailyStore(uid, dailyDateKey);
    const sync = () => setDailySnapshot(getDashboardDailySnapshot(uid, dailyDateKey));
    sync();
    return subscribeDashboardDailyStore(sync);
  }, [uid, dailyDateKey]);

  const dailyActivity: DailyActivityState | null = dailySnapshot.activity;

  const entries = useMemo(() => {
    const merged = [...(catchLog || []), ...(catchDiaryEntries || [])];
    const seen = new Set<string>();

    return merged.filter((row: any, index: number) => {
      const id = String(
        row?.sourceCatchId ||
          row?.catchId ||
          row?.id ||
          '',
      ).trim();

      const timestamp = Number(
        row?.createdAtMs ||
          row?.timestamp ||
          row?.createdAt ||
          row?.date ||
          0,
      ) || 0;
      const species = String(row?.species || row?.fishSpecies || row?.speciesName || '').trim().toLowerCase();
      const water = String(row?.waterName || row?.locationName || row?.spotName || row?.location || '').trim().toLowerCase();
      const fallback = timestamp || species || water ? `${timestamp}|${species}|${water}` : `row:${index}`;
      const key = id ? `id:${id}` : `fallback:${fallback}`;

      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [catchLog, catchDiaryEntries]);

  const stats = useMemo(() => {
    const profileStats = (userProfile as any)?.stats || {};
    const reputationTracks = (userProfile as any)?.reputation?.tracks || {};
    const catches =
      entries.length ||
      Number(profileStats?.catches || 0) ||
      Number(profileStats?.totalCatches || 0) ||
      Number((userProfile as any)?.totalCatches || 0) ||
      0;
    const species =
      uniqueCount(entries, (row) => row?.species || row?.fishSpecies || row?.speciesName) ||
      Number(profileStats?.species || 0) ||
      Number(profileStats?.uniqueSpeciesCount || 0) ||
      0;
    const waters =
      uniqueCount(entries, (row) => row?.waterName || row?.locationName || row?.spotName || row?.location) ||
      Number(profileStats?.waters || 0) ||
      Number(profileStats?.uniqueWatersCount || 0) ||
      Number(profileStats?.spots || 0) ||
      Number(reputationTracks?.explorer?.value || 0) ||
      0;
    const savedSpots = Array.isArray((userProfile as any)?.savedSpots) ? (userProfile as any).savedSpots.length : 0;
    const communityPosts =
      Number(profileStats?.communityPosts || 0) ||
      Number(profileStats?.posts || 0) ||
      Number(profileStats?.postsCount || 0) ||
      Number(reputationTracks?.community_contributor?.value || 0) ||
      0;
    const completed = Object.keys(completionsByKey || {}).length || Number((userProfile as any)?.completedChallengesCount || 0) || 0;
    const achievements =
      Object.keys(achievementMap || {}).length ||
      (Array.isArray((userProfile as any)?.achievements) ? (userProfile as any).achievements.length : 0);

    return { catches, species, waters, savedSpots, communityPosts, completed, achievements };
  }, [achievementMap, completionsByKey, entries, userProfile]);

  const firstName = String((userProfile as any)?.displayName || (userProfile as any)?.username || (userProfile as any)?.name || 'Captain').split(/\s+/)[0];
  const xp = Number((userProfile as any)?.xp ?? (userProfile as any)?.points ?? 0) || 0;
  const shellBalance = Math.max(0, Number((userProfile as any)?.shells ?? 0));
  const lifetimeShells = Math.max(shellBalance, Number((userProfile as any)?.lifetimeShells ?? shellBalance));
  const xpInfo = xpProgress(xp);
  const level = xpInfo.level;
  const levelPct = Math.round(xpInfo.percent);

  const activeProgress = useMemo(() => {
    return Object.entries(progressByKey || {})
      .map(([key, value]) => ({ key, ...(value as any) }))
      .filter((entry: any) => !entry.completed && Number(entry?.target ?? entry?.goal ?? 0) > 0)
      .sort((a: any, b: any) => pct(b.current ?? b.value ?? b.progress, b.target ?? b.goal) - pct(a.current ?? a.value ?? a.progress, a.target ?? a.goal))
      .slice(0, 3);
  }, [progressByKey]);

  const theme = isDarkMode
    ? {
        pageText: '#F2F6FC',
        title: '#F7FAFF',
        body: '#A9B6C9',
        muted: '#8392A9',
        panel: '#0D1626',
        panelSoft: '#101A2B',
        card: '#121D2F',
        cardSoft: '#0F1929',
        cardText: '#F2F6FC',
        cardMuted: '#93A1B5',
        border: 'rgba(132,151,190,0.16)',
        borderStrong: 'rgba(132,151,190,0.24)',
        accent: '#7A8FF0',
        accentSoft: 'rgba(122,143,240,0.14)',
        cyan: '#67D8E8',
        cyanSoft: 'rgba(103,216,232,0.12)',
        success: '#62D7A3',
        successSoft: 'rgba(98,215,163,0.12)',
        shell: '#F0C86D',
        shellSoft: 'rgba(240,200,109,0.12)',
        shadow: '0 10px 30px rgba(0,0,0,0.20)',
        cardShadow: '0 5px 16px rgba(0,0,0,0.14)',
      }
    : {
        pageText: '#2F3C45',
        title: '#243640',
        body: '#5C6970',
        muted: '#9A8168',
        panel: '#FFFDF8',
        panelSoft: '#F8F1E7',
        card: '#FFFCF7',
        cardSoft: '#FBF5EC',
        cardText: '#34424C',
        cardMuted: '#71808A',
        border: 'rgba(91,67,47,0.13)',
        borderStrong: 'rgba(91,67,47,0.20)',
        accent: '#2F80ED',
        accentSoft: 'rgba(47,128,237,0.09)',
        cyan: '#48AFC4',
        cyanSoft: 'rgba(72,175,196,0.10)',
        success: '#3DBB86',
        successSoft: 'rgba(61,187,134,0.10)',
        shell: '#C9952F',
        shellSoft: 'rgba(201,149,47,0.10)',
        shadow: '0 10px 26px rgba(89,65,37,0.07)',
        cardShadow: '0 4px 14px rgba(89,65,37,0.05)',
      };

  const isFreshStart = stats.catches === 0 && stats.species === 0 && (stats.waters || stats.savedSpots) === 0 && stats.communityPosts === 0;

  const todayActions = dailyActivity?.actions || {};
  const rawMissions = [
    {
      key: 'map',
      done: Number(todayActions.search_area || 0) > 0,
      icon: '🌊',
      title: 'Scout Big Blue',
      body: Number(todayActions.search_area || 0) > 0
        ? 'Area searched today — your water check is on the board.'
        : 'Search one promising area and get a read on the water.',
      cta: 'Scout',
      onClick: onOpenMap,
    },
    {
      key: 'fishiq',
      done: Number(todayActions.open_fishiq || 0) > 0,
      icon: '🧠',
      title: 'Read the water',
      body: Number(todayActions.open_fishiq || 0) > 0
        ? 'Water read checked today — carry one useful clue to the water.'
        : 'Open the water read and review today’s local clues.',
      cta: 'Water Read',
      onClick: onOpenFishIQ,
    },
    {
      key: 'log',
      done: Number(todayActions.log_catch || 0) > 0,
      icon: '📘',
      title: "Feed Capt’n’s Log",
      body: Number(todayActions.log_catch || 0) > 0
        ? 'Today’s catch or observation is safely in the log.'
        : 'Log a catch, miss, sighting, or useful observation.',
      cta: 'Log',
      onClick: onOpenLogCatch,
    },
    {
      key: 'bay',
      done: Number(todayActions.share_to_bay || 0) > 0,
      optional: true,
      icon: '🏝️',
      title: 'Share with Baleen Bay',
      body: Number(todayActions.share_to_bay || 0) > 0
        ? 'Community signal shared today.'
        : 'Optional: share something useful without exposing a private spot.',
      cta: 'Bay',
      onClick: onOpenCommunity,
    },
  ];

  const firstOpenIndex = rawMissions.findIndex((mission) => !mission.done);
  const nextIndex = firstOpenIndex === -1 ? 0 : firstOpenIndex;
  const missions = rawMissions.map((mission, index) => ({
    ...mission,
    status: mission.done ? 'done' : index === nextIndex ? 'next' : 'waiting',
  }));
  const coreMissions = missions.filter((mission: any) => !mission.optional);
  const missionDoneCount = coreMissions.filter((mission) => mission.done).length;
  const missionPct = Math.round((missionDoneCount / Math.max(1, coreMissions.length)) * 100);
  const coreLoopComplete = missionDoneCount === coreMissions.length;

  const statCards = [
    { label: 'Catches', value: stats.catches, icon: '🎣', onClick: onOpenLogCatch },
    { label: 'Species', value: stats.species, icon: '🐟', onClick: onOpenFishIQ },
    { label: 'Waters', value: stats.waters || stats.savedSpots, icon: '📍', onClick: onOpenMap },
    { label: 'Bay', value: stats.communityPosts, icon: '🏝️', onClick: onOpenCommunity },
  ];

  const sectionStyle: React.CSSProperties = {
    background: theme.panel,
    borderColor: theme.border,
    boxShadow: theme.shadow,
  };

  const cardStyle: React.CSSProperties = {
    background: theme.card,
    borderColor: theme.border,
    boxShadow: theme.cardShadow,
    color: theme.cardText,
  };

  return (
    <RoddoPageShell padding="none" contentClassName="max-w-4xl px-0 pb-28 roddo-dashboard-lane">
      <div className="space-y-3.5 px-3 pb-4 pt-3 min-[390px]:px-4 sm:px-5" style={{ color: theme.pageText }}>
        <section className="relative overflow-hidden rounded-[20px] border p-4 sm:p-5" style={sectionStyle}>
          <div
            className="pointer-events-none absolute inset-x-0 top-0 h-[3px]"
            style={{ background: `linear-gradient(90deg, ${theme.cyan}, ${theme.accent}, transparent 78%)` }}
          />

          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="inline-flex items-center gap-2 rounded-full border px-2.5 py-1 text-[10px] font-black uppercase tracking-[0.22em]"
                style={{ background: theme.cyanSoft, borderColor: theme.border, color: theme.muted }}>
                <span>🧭</span>
                <span>Roddo Today</span>
              </div>
              <h1 className="mt-3 text-[26px] font-black leading-[1.05]" style={{ color: theme.title }}>
                {isFreshStart ? 'Your first bite plan' : `${firstName}’s bite plan`}
              </h1>
              <p className="mt-2 max-w-2xl text-[13px] font-semibold leading-6" style={{ color: theme.body }}>
                {isFreshStart
                  ? 'Scout one water, read one clue, then log what really happened. Roddo gets smarter from useful fishing history.'
                  : 'Scout water, read the clues, log what happened, and keep your fishing history moving.'}
              </p>
            </div>

            <div className="flex shrink-0 items-center gap-2">
              <button
                type="button"
                onClick={onOpenFishIQ}
                className="rounded-[13px] border px-3 py-2 text-xs font-black text-white transition hover:-translate-y-px active:translate-y-0"
                style={{ background: theme.accent, borderColor: theme.borderStrong, boxShadow: theme.cardShadow }}
              >
                🧠 Water Read
              </button>
              <button
                type="button"
                onClick={onBack}
                aria-label="Back to Big Blue"
                title="Back to Big Blue"
                className="grid h-10 w-10 place-items-center rounded-full border text-lg font-black transition hover:-translate-y-px active:translate-y-0"
                style={{ background: theme.card, borderColor: theme.border, color: theme.cardText, boxShadow: theme.cardShadow }}
              >
                ‹
              </button>
            </div>
          </div>

          <div className="mt-4 grid gap-2.5 sm:grid-cols-3">
            <div className="rounded-[16px] border p-3.5" style={cardStyle}>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="text-[10px] font-black uppercase tracking-[0.18em]" style={{ color: theme.cardMuted }}>Angler growth</div>
                  <div className="mt-1.5 text-[22px] font-black">Level {compactNumber(level)}</div>
                </div>
                <div className="rounded-[11px] px-2.5 py-1.5 text-[11px] font-black" style={{ background: theme.accentSoft, color: theme.accent }}>
                  {compactNumber(xp)} XP
                </div>
              </div>
              <div className="mt-3 h-2 overflow-hidden rounded-full" style={{ background: isDarkMode ? 'rgba(255,255,255,.10)' : 'rgba(30,41,59,.09)' }}>
                <div className="h-full rounded-full" style={{ width: `${levelPct}%`, background: theme.accent }} />
              </div>
              <p className="mt-2 text-[11px] font-bold leading-5" style={{ color: theme.cardMuted }}>
                Keep building real history — useful notes beat fake pressure.
              </p>
            </div>

            <button
              type="button"
              onClick={onOpenHold}
              className="rounded-[16px] border p-3.5 text-left transition hover:-translate-y-px active:translate-y-0"
              style={{ ...cardStyle, background: theme.shellSoft }}
            >
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="text-[10px] font-black uppercase tracking-[0.18em]" style={{ color: theme.cardMuted }}>Shell wallet</div>
                  <div className="mt-1.5 text-[28px] font-black">{compactNumber(shellBalance)}</div>
                </div>
                <span className="text-xl">🐚</span>
              </div>
              <div className="mt-1 text-[11px] font-bold" style={{ color: theme.cardMuted }}>
                {compactNumber(lifetimeShells)} lifetime earned
              </div>
              <div className="mt-3 inline-flex rounded-[10px] px-2.5 py-1 text-[10px] font-black" style={{ background: theme.card, color: theme.cardText }}>
                Open The Hold →
              </div>
            </button>

            <div className="rounded-[16px] border p-3.5" style={cardStyle}>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="text-[10px] font-black uppercase tracking-[0.18em]" style={{ color: theme.cardMuted }}>Collection</div>
                  <div className="mt-1.5 text-[28px] font-black">{compactNumber(stats.achievements + stats.completed)}</div>
                </div>
                <span className="text-xl">🏆</span>
              </div>
              <div className="mt-1 text-[11px] font-bold" style={{ color: theme.cardMuted }}>
                {isFreshStart ? 'Ready to unlock' : 'earned moments'}
              </div>
            </div>
          </div>
        </section>

        <section className="rounded-[20px] border p-3.5 sm:p-4" style={{ ...sectionStyle, background: theme.panelSoft }}>
          <div className="flex items-center justify-between gap-3">
            <div>
              <div className="text-[10px] font-black uppercase tracking-[0.22em]" style={{ color: theme.muted }}>Today’s mission</div>
              <h2 className="mt-1 text-lg font-black" style={{ color: theme.title }}>
                {coreLoopComplete ? 'Loop complete — nice cast' : isFreshStart ? 'Start with one small move' : 'Make one useful fishing move'}
              </h2>
              <p className="mt-1 text-[11px] font-bold" style={{ color: theme.body }}>
                {missionDoneCount}/{coreMissions.length} core actions complete today
              </p>
            </div>
            <button
              type="button"
              onClick={onOpenChallenges}
              className="rounded-[12px] border px-3 py-2 text-[11px] font-black transition hover:-translate-y-px active:translate-y-0"
              style={{ background: theme.card, borderColor: theme.border, color: theme.cardText }}
            >
              Goals 🏁
            </button>
          </div>

          <div className="mt-3 h-2 overflow-hidden rounded-full" style={{ background: isDarkMode ? 'rgba(255,255,255,.09)' : 'rgba(30,41,59,.08)' }}>
            <div className="h-full rounded-full transition-all" style={{ width: `${missionPct}%`, background: theme.accent }} />
          </div>

          <div className="mt-3 grid gap-2.5 sm:grid-cols-2">
            {missions.map((mission) => {
              const isDone = mission.status === 'done';
              const isNext = mission.status === 'next';
              const isOptional = Boolean((mission as any).optional);
              const statusLabel = isDone ? 'Done' : isOptional ? 'Optional' : isNext ? 'Next up' : 'Queued';
              const stateBg = isDone ? theme.successSoft : isNext ? theme.accentSoft : theme.cardSoft;
              const stateColor = isDone ? theme.success : isNext ? theme.accent : theme.cardMuted;

              return (
                <button
                  key={mission.key}
                  type="button"
                  onClick={mission.onClick}
                  className="rounded-[16px] border p-3 text-left transition hover:-translate-y-px active:translate-y-0"
                  style={{ ...cardStyle, background: stateBg }}
                >
                  <div className="flex items-start gap-3">
                    <div className="grid h-9 w-9 shrink-0 place-items-center rounded-[11px] text-base" style={{ background: theme.card }}>
                      {isDone ? '✓' : mission.icon}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center justify-between gap-2">
                        <h3 className="truncate text-[13px] font-black">{mission.title}</h3>
                        <span className="shrink-0 rounded-full px-2 py-1 text-[9px] font-black uppercase tracking-[0.12em]" style={{ background: theme.card, color: stateColor }}>
                          {statusLabel}
                        </span>
                      </div>
                      <p className="mt-1.5 text-[11px] font-bold leading-5" style={{ color: theme.cardMuted }}>{mission.body}</p>
                      <div className="mt-2 inline-flex text-[10px] font-black" style={{ color: stateColor }}>
                        {isDone ? 'Review' : isFreshStart && isNext ? 'Start here' : mission.cta} →
                      </div>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </section>

        <div className="grid grid-cols-4 gap-2">
          {statCards.map((card) => (
            <button
              key={card.label}
              type="button"
              onClick={card.onClick}
              className="rounded-[15px] border p-3 text-left transition hover:-translate-y-px active:translate-y-0"
              style={cardStyle}
            >
              <div className="text-base">{card.icon}</div>
              <div className="mt-1.5 text-[22px] font-black leading-none">{compactNumber(card.value)}</div>
              <div className="mt-1.5 truncate text-[9px] font-black uppercase tracking-[0.15em]" style={{ color: theme.cardMuted }}>{card.label}</div>
            </button>
          ))}
        </div>

        <section className="rounded-[20px] border p-3.5 sm:p-4" style={sectionStyle}>
          <div className="flex items-center justify-between gap-3">
            <div>
              <div className="text-[10px] font-black uppercase tracking-[0.22em]" style={{ color: theme.muted }}>Active goals</div>
              <h2 className="mt-1 text-lg font-black" style={{ color: theme.title }}>Keep the streak warm</h2>
            </div>
            <button
              type="button"
              onClick={onOpenAchievements}
              className="rounded-[12px] border px-3 py-2 text-[11px] font-black transition hover:-translate-y-px active:translate-y-0"
              style={{ background: theme.card, borderColor: theme.border, color: theme.cardText }}
            >
              Trophy case 🏆
            </button>
          </div>

          {activeProgress.length > 0 ? (
            <div className="mt-3 space-y-2.5">
              {activeProgress.map((entry: any) => {
                const current = Number(entry.current ?? entry.value ?? entry.progress ?? 0) || 0;
                const target = Number(entry.target ?? entry.goal ?? 0) || 0;
                const rowPct = pct(current, target);
                return (
                  <div key={entry.key} className="rounded-[14px] border p-3" style={{ background: theme.cardSoft, borderColor: theme.border }}>
                    <div className="flex items-center justify-between gap-3 text-[12px] font-black">
                      <span className="truncate">{resolveChallengeTitle(entry)}</span>
                      <span className="shrink-0" style={{ color: theme.cardMuted }}>{compactNumber(current)}/{compactNumber(target)}</span>
                    </div>
                    <div className="mt-2 h-2 overflow-hidden rounded-full" style={{ background: isDarkMode ? 'rgba(255,255,255,.09)' : 'rgba(30,41,59,.08)' }}>
                      <div className="h-full rounded-full" style={{ width: `${rowPct}%`, background: theme.accent }} />
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="mt-3 rounded-[15px] border p-3.5" style={{ background: theme.cardSoft, borderColor: theme.border }}>
              <p className="text-[12px] font-bold leading-5" style={{ color: theme.cardMuted }}>
                {isFreshStart
                  ? 'Scout Big Blue first. Logs, goals, and milestones will start building from real fishing history.'
                  : 'No active goal pressure. Pick one useful move above and Roddo will keep the loop warm.'}
              </p>
            </div>
          )}
        </section>
      </div>
    </RoddoPageShell>
  );
};

export default Dashboard;
